import { motion } from "framer-motion";
import { Target, Heart, Users, Shield, Zap, Globe } from "lucide-react";
import { LeadForm } from "../components/sections/LeadForm";
import { SEO } from "../components/common/SEO";
import { SEO_DATA } from "../data/seoData";

export default function AboutPage() {
  return (
    <div>
      <SEO 
        title={SEO_DATA.about.title}
        description={SEO_DATA.about.description}
        keywords={SEO_DATA.about.keywords}
        canonical="https://switchyourvendor.com/about"
      />
      <section className="pt-20 pb-14 relative" style={{ background: "linear-gradient(135deg, #fff5f5 0%, #ffffff 50%, #f0f4ff 100%)" }}>
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="text-red-600 font-bold text-xs tracking-[0.2em] uppercase font-body">About Us</span>
            <h1 className="font-heading text-4xl sm:text-5xl font-black mt-3 mb-4 tracking-tight">
              India's First Dedicated<br /><span className="bg-gradient-to-r from-red-600 to-rose-500 bg-clip-text text-transparent">Vendor Switching Platform</span>
            </h1>
            <p className="text-slate-500 text-base md:text-lg leading-relaxed font-body">
              SwitchYourVendor was founded with a simple belief: businesses deserve better vendors, and switching shouldn't be a nightmare. We're building India's most trusted platform to help SMEs find, compare, and switch to better service providers.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Target, title: "Our Mission", desc: "To empower every Indian business with access to verified, quality vendors across compliance, IT, supplies, and manpower — at the best possible price.", color: "#dc2626" },
              { icon: Heart, title: "Our Vision", desc: "A world where switching vendors is as easy as switching apps. No lock-ins, no disruption — just better service, every time.", color: "#6366f1" },
              { icon: Users, title: "Who We Serve", desc: "From 5-person startups to 500-person enterprises, we serve businesses that want to optimize vendor relationships and reduce operational overhead.", color: "#10b981" },
            ].map(({ icon: Icon, title, desc, color }, i) => (
              <motion.div key={title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-slate-50 rounded-2xl p-7 border border-slate-100">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${color}10` }}>
                  <Icon size={22} style={{ color }} />
                </div>
                <h3 className="text-lg font-extrabold text-slate-900 mb-2 font-body">{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed font-body">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-red-600 font-bold text-xs tracking-[0.2em] uppercase font-body">Why We Exist</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-black mt-3 tracking-tight">The Problem We Solve</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Shield, title: "Vendor Lock-in", desc: "Most businesses are stuck with underperforming vendors due to complicated contracts." },
              { icon: Zap, title: "No Transparency", desc: "Pricing, quality, and service levels are opaque — making comparison nearly impossible." },
              { icon: Globe, title: "Too Many Vendors", desc: "Managing 10+ vendors for different services creates chaos and inefficiency." },
              { icon: Users, title: "Switching is Hard", desc: "Even when businesses want to switch, the transition process is painful and risky." },
            ].map(({ icon: Icon, title, desc }, i) => (
              <motion.div key={title} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="bg-white rounded-2xl p-6 border border-slate-100 text-center">
                <Icon size={24} className="text-red-500 mx-auto mb-3" />
                <h3 className="text-sm font-extrabold text-slate-900 mb-1.5 font-body">{title}</h3>
                <p className="text-[13px] text-slate-500 font-body">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[["500+", "Clients Served"], ["10K+", "Vendor Switches"], ["30%", "Avg Cost Savings"], ["4.9/5", "Client Rating"]].map(([val, label]) => (
            <div key={label}>
              <div className="font-heading text-4xl font-black text-red-600">{val}</div>
              <div className="text-sm text-slate-500 mt-1 font-body">{label}</div>
            </div>
          ))}
        </div>
      </section>

      <section id="contact" className="py-14 bg-slate-50">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-red-600 font-bold text-xs tracking-[0.2em] uppercase font-body">Get in Touch</span>
            <h2 className="font-heading text-3xl font-black mt-3 tracking-tight">Let's Work Together</h2>
          </div>
          <LeadForm />
        </div>
      </section>
    </div>
  );
}
